import React, { useEffect, useState } from 'react';

import FloatValidationLabel from './FloatValidationLabel';
import PropTypes from 'prop-types';
import postData from '../../../utilities/postData';
import usePrevious from '../../../utilities/usePrevious';

const SelectList = ({
    customUrl,
    filter,
    lookupType,
    name,
    onChange,
    options: passThroughOptions,
    placeholder,
    validationError,
    value,
    ...passThroughProps
}) => {
    const [data, setData] = useState({ items: [] });
    const previousFilter = usePrevious(filter);

    useEffect(() => {
        //verify something actually changed in the filter before making a new network call
        if (filter === undefined || (JSON.stringify(previousFilter) !== JSON.stringify(filter))) {
            //when sending options as children, we want to set lookupType to undefined
            if (lookupType !== undefined) {
                const postFilter = {
                    franchiseId: window.biztrax.franchiseId,
                    lookupType: lookupType,
                    selectedIds: [value],
                    pageSize: 1000,
                    ...filter
                };
                postData(customUrl || '/api/lookups/selectOptions', postFilter, setData);
            }
        };
    }, [filter]);

    function combinedOptions() {
        const optionItems = Array.isArray(data.items) ? [...data.items] : [];
        if (passThroughOptions !== undefined) {
            return passThroughOptions.concat(optionItems);
        }
        return optionItems;
    }

    const options = combinedOptions().map((item, index) =>
        <option
            disabled={typeof item.isActive === "boolean" && !item.isActive}
            key={index}
            value={item.id}
        >{item.name}</option>
    );

    const placeHolderOption = !combinedOptions().find(x => x.id === value)
        ? (<option key={-1} disabled value="">{placeholder}</option>) : undefined;

    //converts a number value to int type otherwise sends string value
    const updateData = ({ target: { value } }) => {
        if (isNaN(value)) {
            onChange({ target: { name: name, value: value } });
        } else {
            onChange({ target: { name: name, value: parseInt(value) } });
        }
    }

    //gets width style from props and passes it to the wrapper div
    //prevents styled drop arrow from being misplaced
    const widthStyle = () => {
        if (!passThroughProps.style) return null;
        return {
            width: passThroughProps.style.width,
            maxWidth: passThroughProps.style.maxWidth,
            minWidth: passThroughProps.style.minWidth
        }
    }

    //removes width style from props on select element
    //prevents styled drop arrow from being misplaced
    const passThroughPropsNoWidth = () => {
        const newProps = { ...passThroughProps };
        if (!newProps.style) return newProps;
        if (newProps.style.width) delete newProps.style.width;
        if (newProps.style.maxWidth) delete newProps.style.maxWidth;
        if (newProps.style.minWidth) delete newProps.style.minWidth;
        return newProps;
    }

    return (
        <div className="wrapper" style={widthStyle()}>
            <span className="biz-select">
                <React.Fragment>
                    <FloatValidationLabel validationError={validationError} />
                    <select
                        {...passThroughPropsNoWidth()}
                        className='form-control'
                        onChange={updateData}
                        name={name}
                        value={value || ''}
                    >
                        {placeHolderOption}
                        {passThroughProps.children}
                        {options}
                    </select>
                </React.Fragment>
            </span>
        </div>
    );
}

SelectList.defaultProps = {
    placeholder: ''
}

SelectList.propTypes = {
    onChange: PropTypes.func,
    lookupType: PropTypes.string,
    placeholder: PropTypes.string,
    required: PropTypes.bool,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    validationError: PropTypes.string
};

export default SelectList;